import { changeLanguage } from '@getpopsure/i18n-react';
import { useFlag } from '@unleash/proxy-client-react';
import { DelinquencyBlockerPage } from 'components/DelinquencyBlocker';
import PageNotFound from 'components/pageNotFound';
import routes from 'constants/routes';
import AuthenticatedRoute from 'containers/authenticatedRoute';
import {
  Account,
  AccountSettings,
  BillingAndPayment,
  MailingPreferences,
  PrivacyAndTerms,
} from 'features/accountV2';
import { SignupPage as Bike } from 'features/bike';
import { BikeClaims } from 'features/bikeClaims';
import { BillingHistory } from 'features/billingHistory';
import BrokerMandate from 'features/brokerMandate/containers';
import { ClaimAssessmentDetails } from 'features/claimAssessments/ClaimAssessmentDetails.container';
import GenericClaimConfirmation from 'features/claims/components/confirmation';
import Claims from 'features/claims/containers';
import LegalConfirmation from 'features/claims/containers/legalClaim/confirmation';
import LegalClaimConfirmInfo from 'features/claims/containers/legalClaim/confirmInfo';
import LegalDescribeClaim from 'features/claims/containers/legalClaim/describeClaim';
import LegalProcessing from 'features/claims/containers/legalClaim/processing';
import ClaimStatus from 'features/claimsProcess/claimStatus';
import ClaimSteps from 'features/claimsProcess/claimSteps';
import ClaimTypes from 'features/claimsProcess/claimTypes';
import ClaimsConfirmation from 'features/claimsProcess/confirmation';
import MyClaims from 'features/claimsProcess/myClaims';
import ClaimsPaymentInfo from 'features/claimsProcess/paymentInfo';
import ClaimsProcessing from 'features/claimsProcess/processing';
import ClaimsUploadDocuments from 'features/claimsProcess/uploadDocuments';
import ClaimsDashboard from 'features/claimsV2';
import { ClaimDetail } from 'features/claimsV2/components/claimDetail';
import { CreateClaim } from 'features/claimsV2/components/createClaim';
import { CommonClaims } from 'features/commonClaims/CommonClaims';
import { CommpanyHealthClaims } from 'features/companyHealthClaims';
import { SignupPage as DentalSignup } from 'features/dental';
import { DentalClaims } from 'features/dentalClaims';
import { Signup as DisabilitySignup } from 'features/disability';
import { DisabilitySuccessScreen } from 'features/disability/DisabilitySuccessScreen';
import ExpatSignupPage from 'features/expat';
import { ExpatClaims } from 'features/expatClaims/ExpatClaims';
import { ExpatEuClaims } from 'features/expatEuClaims/ExpatEuClaims';
import { ExpatLongTermClaims } from 'features/expatLongTermClaims/ExpatLongTermClaims';
import { ExpatSpainRouter } from 'features/expatSpain/ExpatSpainRouter';
import { ExpatSpainClaims } from 'features/expatSpainClaims/ExpatSpainClaims';
import { AdjustPolicyFlow } from 'features/generalQuestionnaire/adjustPolicy/AdjustPolicyFlow';
import { AdjustPolicyPreview } from 'features/generalQuestionnaire/adjustPolicy/preview';
import { GeneralClaimsPreview } from 'features/generalQuestionnaire/generalClaims/preview';
import { PreviewSignupPage } from 'features/generalQuestionnaire/generalPolicy/preview';
import { QuestionnairePreview } from 'features/generalQuestionnaire/preview';
import HealthCardPhoto from 'features/healthCard/mobilePhotoUpload';
import { SignupPage as HouseholdSignupPage } from 'features/household';
import { HouseholdClaims } from 'features/householdClaims';
import { SignupPage as LegalSignup } from 'features/legal';
import { Claims as LegalClaimsAndConsultations } from 'features/legalClaims';
import Liability from 'features/liability';
import { LiabilityClaims } from 'features/liabilityClaims';
import { AddBeneficiaries } from 'features/lifeBeneficiaries';
import { EditBeneficiaries } from 'features/lifeBeneficiaries/components/EditBeneficiariesWrapper';
import LifeFrance from 'features/lifeFrance';
import Base from 'features/main';
import InsuranceOptions from 'features/main/insuranceOptions';
import {
  AddPaymentMethod,
  BillingScreen,
} from 'features/paymentMethods/containers';
import { StripeRedirectGateway } from 'features/paymentMethods/containers/StripeRedirectGateway/StripeRedirectGateway.container';
import PaymentScreen from 'features/paymentScreen/paymentScreen.router';
import { SignupPage as PetHealthSignup } from 'features/petHealth';
import { PoliciesDashboard } from 'features/policiesDashboard';
import { PoliciesList } from 'features/policiesDashboard/modules/PoliciesList';
import { Signup as PrivatePreSignup } from 'features/privateHealthPreSignup';
import { FetchQuote as PrivateHealthFetchQuote } from 'features/privateHealthPreSignup/components/FetchQuote';
import { RecommendationTool } from 'features/recommendationTool';
import { InsuranceToolRoutes } from 'features/recommendationTool/newRoutes';
import { ReferralPayoutDetails } from 'features/referralEngine';
import { SeedQuestionnaire } from 'features/seedQuestionnaire/SeedQuestionnaire';
import { SignIn } from 'features/signIn';
import { SignupRouting } from 'features/signUp';
import { Unsubscribed } from 'features/subscription/components/Unsubscribed';
import TaxStatements from 'features/taxStatements';
import { SignupTally as TravelHealthFranceSignupTally } from 'features/travelHealthFrance';
import { VideoDoctorAppointment } from 'features/videoDoctorAppointment';
import { useQueryParamValue } from 'hooks/useQueryParamValue';
import { cookieDomainOptions } from 'locales';
import { memo, useEffect } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { validateLocale } from 'shared/i18n';
import { isStagingOrDev } from 'shared/util/isStagingOrDev';

import { SignupPage as DisabilityV2 } from './features/disabilityV2';
import { SignupPage as DogLiabilitySpain } from './features/dogLiabilitySpain';
import { ExpatEuRouter as ExpatEu } from './features/expatEu';
import { ExpatLongTermRouter as ExpatLongTerm } from './features/expatLongTerm';
import { GeneralClaims } from './features/generalQuestionnaire/generalClaims';
import { SignupPage as GeneralSignup } from './features/generalQuestionnaire/generalPolicy';
import { SignupPage as GeneralDependentSignup } from './features/generalQuestionnaire/generalPolicy/dependent';
import { HomeFranceTallyForm } from './features/homeFrance/HomeFranceTallyForm';
import { SignupPage as IncomeProtectionSignup } from './features/incomeProtection';
import { SignupPage as LifeInsurance } from './features/life';
import { SignupPage as Pension } from './features/pension';
import { PolicySingle } from './features/policySingle';
import { PrivateHealthClaimsRouter } from './features/privateHealthClaims/PrivateHealthClaimsRouter';
import { UpdateEmail } from './features/updateEmail';

const Routes = () => {
  const RedirectToInitialScreen = () => {
    const { search } = useLocation();
    return <Redirect to={`${routes.me.policies.path}${search}`} />;
  };

  const isIncomeProtection = useFlag('app_income_protection');
  const isDisabilityV2 = useFlag('app_disability_v1');
  const isCommonClaims = useFlag('app_common_claims');
  const isAdjustPolicy = useFlag('app_adjust_policy');
  const useAllowUpdateEmailAddress = useFlag('app_allow_update_email');

  const language = useQueryParamValue('lang');

  useEffect(() => {
    if (language) {
      const locale = validateLocale(language);
      changeLanguage(locale, cookieDomainOptions);
    }
  }, [language]);

  return (
    <Switch>
      <Route exact={true} path={routes.base.path} component={Base} />
      <Route
        exact={true}
        path={routes.base.insuranceOptions.path}
        component={InsuranceOptions}
      />
      <Route
        exact={true}
        path={routes.base.insuranceTools.path}
        render={() => <Redirect to={routes.insuranceTool.path} />}
      />
      <Route
        path={routes.checkup.path}
        render={() => <Redirect to={routes.insuranceTool.path} />}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.path}
        component={RedirectToInitialScreen}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.account.path}
        component={Account}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.accountSettings.path}
        component={AccountSettings}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.communicationPreferences.path}
        component={MailingPreferences}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.brokerMandate.sign.path}
        component={BrokerMandate}
      />
      <Route path={routes.account.signIn.path} component={SignIn} />
      <Route path={routes.account.signUp.path} component={SignupRouting} />
      <Route
        path={routes.policies.petHealth.path}
        component={PetHealthSignup}
      />
      <Route
        path={routes.healthInsuranceTool.path}
        component={RecommendationTool}
      />
      <Route
        exact={true}
        path={routes.me.policies.list.path}
        component={PoliciesList}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.policies.path}
        component={PoliciesDashboard}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.policies.detail.path}
        component={PolicySingle}
      />
      <AuthenticatedRoute
        exact
        path={routes.me.policies.detail.studentChecklist.path}
        component={PolicySingle}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.claims.path}
        component={ClaimsDashboard}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.path}
        component={Claims}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.confirmationGeneric.path}
        component={GenericClaimConfirmation}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.legalClaim.confirmInfo.path}
        component={LegalClaimConfirmInfo}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.legalClaim.describeClaim.path}
        component={LegalDescribeClaim}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.legalClaim.processing.path}
        component={LegalProcessing}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.legalClaim.confirmation.path}
        component={LegalConfirmation}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.myClaims.path}
        component={MyClaims}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.claimStatus.path}
        component={ClaimStatus}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.claimSteps.path}
        component={ClaimSteps}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.claimTypes.path}
        component={ClaimTypes}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.uploadDocuments.path}
        component={ClaimsUploadDocuments}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.paymentInfo.path}
        component={ClaimsPaymentInfo}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.processing.path}
        component={ClaimsProcessing}
      />
      <AuthenticatedRoute
        exact={true}
        path={routes.me.claims.confirmation.path}
        component={ClaimsConfirmation}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.liability.path}
        component={LiabilityClaims}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.dental.path}
        component={DentalClaims}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.companyHealth.path}
        component={CommpanyHealthClaims}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.policies.expat.videoDoctorAppointment.path}
        component={VideoDoctorAppointment}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.household.path}
        component={HouseholdClaims}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.expat.path}
        component={ExpatClaims}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.expatEu.path}
        component={ExpatEuClaims}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.expatLongTerm.path}
        component={ExpatLongTermClaims}
      />
      {/* Should probably be hidden behind a feature flag */}
      {isCommonClaims && (
        <AuthenticatedRoute
          exact={false}
          path={routes.claims.common.path}
          component={CommonClaims}
        />
      )}

      <AuthenticatedRoute
        exact={false}
        path={routes.claims.general.path}
        component={GeneralClaims}
      />
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.expatSpain.path}
        component={ExpatSpainClaims}
      />
      <Route
        // Redirects all incoming routes to new flow
        exact={false}
        path="/policies/incoming"
        component={() => <Redirect to={routes.policies.expat.path} />}
      />
      <Route
        path={routes.policies.household.path}
        component={HouseholdSignupPage}
      />
      {!isDisabilityV2 && (
        <AuthenticatedRoute
          exact={true}
          path={routes.policies.disability.success.path}
          component={DisabilitySuccessScreen}
        />
      )}
      <AuthenticatedRoute
        exact={false}
        path={routes.claims.legal.path}
        component={LegalClaimsAndConsultations}
      />

      {!isDisabilityV2 && (
        <Route
          path={routes.policies.disability.path}
          component={DisabilitySignup}
        />
      )}
      {isDisabilityV2 && (
        <Route
          path={routes.policies.disabilityV2.path}
          component={DisabilityV2}
        />
      )}
      <AuthenticatedRoute
        exact={true}
        path={routes.me.paymentMethods.path}
        component={BillingScreen}
      />
      <AuthenticatedRoute
        exact={true}
        component={BillingAndPayment}
        path={routes.account.billingAndPayment.path}
      />
      <AuthenticatedRoute
        exact={true}
        component={BillingHistory}
        path={routes.account.billingHistory.path}
      />
      <AuthenticatedRoute
        exact={true}
        component={PrivacyAndTerms}
        path={routes.account.privacyAndTerms.path}
      />
      <AuthenticatedRoute
        exact={true}
        component={TaxStatements}
        path={routes.account.taxStatements.path}
      />
      <AuthenticatedRoute
        exact={true}
        component={AddPaymentMethod}
        path={routes.me.paymentMethods.add.path}
      />
      <AuthenticatedRoute
        exact={true}
        component={StripeRedirectGateway}
        path={routes.me.paymentMethods.confirmSetup.path}
      />
      <AuthenticatedRoute
        exact={true}
        component={ReferralPayoutDetails}
        path={routes.me.referralPayoutDetails.path}
      />
      {useAllowUpdateEmailAddress && (
        <AuthenticatedRoute
          exact={false}
          component={UpdateEmail}
          path={routes.me.updateEmail.path}
        />
      )}
      <Route path={routes.policies.legal.path} component={LegalSignup} />
      <Route path={routes.policies.dental.path} component={DentalSignup} />
      <Route
        exact
        path={routes.policies.travelHealthFrance.path}
        component={TravelHealthFranceSignupTally}
      />
      <Route
        path={routes.policies.privateHealthV2.fetchQuote.path}
        component={PrivateHealthFetchQuote}
      />
      <Route
        path={routes.policies.privateHealthV2.path}
        component={PrivatePreSignup}
      />
      <AuthenticatedRoute
        exact={false}
        component={PrivateHealthClaimsRouter}
        path={routes.claims.privateHealth.path}
      />
      <Route
        path={routes.policies.expatSpain.path}
        component={ExpatSpainRouter}
      />
      <Route path={routes.policies.expatEu.path} component={ExpatEu} />
      <Route path={routes.policies.expatEu.noRegion.path} component={ExpatEu} />
      <Route
        path={routes.policies.expatLongTerm.path}
        component={ExpatLongTerm}
      />
      <Route
        path={routes.policies.expatLongTerm.noRegion.path}
        component={ExpatLongTerm}
      />
      <Route path={routes.policies.expat.path} component={ExpatSignupPage} />
      <Route path={routes.policies.bike.path} component={Bike} />
      {isIncomeProtection && (
        <Route
          path={routes.policies.incomeProtection.path}
          component={IncomeProtectionSignup}
        />
      )}
      <Route
        path={routes.policies.dogLiabilitySpain.path}
        component={DogLiabilitySpain}
      />
      <AuthenticatedRoute
        exact={false}
        component={BikeClaims}
        path={routes.claims.bike.path}
      />
      <AuthenticatedRoute
        exact={false}
        component={CreateClaim}
        path={routes.claims.create.path}
      />
      <AuthenticatedRoute
        exact={false}
        component={ClaimAssessmentDetails}
        path={routes.claims.assessments.path}
      />
      <AuthenticatedRoute
        exact={false}
        component={ClaimDetail}
        path={routes.claims.detail.path}
      />
      <Route
        path={routes.paymentScreenRegionalised.path}
        component={PaymentScreen}
      />
      <Route path={routes.paymentScreen.path} component={PaymentScreen} />
      <Route
        exact={false}
        path={routes.policies.lifeFrance.path}
        component={LifeFrance}
      />
      <Route path={routes.policies.life.path} component={LifeInsurance} />
      <Route path={routes.healthCardPhoto.path} component={HealthCardPhoto} />
      <Route path={routes.policies.liability.path} component={Liability} />
      <Route path={routes.insuranceTool.path} component={InsuranceToolRoutes} />
      <Route
        path={routes.me.policies.life.beneficiaries.path}
        component={AddBeneficiaries}
      />
      <Route
        path={routes.me.policies.life.beneficiaries.edit.path}
        component={EditBeneficiaries}
      />
      {/* Subscription */}
      <Route
        path={routes.subscriptions.unsubscribed.path}
        component={Unsubscribed}
        exact
      />
      <Route
        path={routes.policies.delinquency.path}
        component={DelinquencyBlockerPage}
      />
      <Route path={routes.policies.seed.path} component={SeedQuestionnaire} />

      <Route path={routes.policies.pension.path} component={Pension} />
      {isStagingOrDev && (
        <Route
          path={routes.policies.general.dependents.path}
          component={GeneralDependentSignup}
        />
      )}
      {isStagingOrDev && (
        <Route
          path={routes.policies.general.noRegion.dependents.path}
          component={GeneralDependentSignup}
        />
      )}
      <Route
        path={routes.policies.homeFrance.validationTally.path}
        component={HomeFranceTallyForm}
      />
      <Route path={routes.policies.general.path} component={GeneralSignup} />
      <Route
        path={routes.policies.general.noRegion.path}
        component={GeneralSignup}
      />

      {isAdjustPolicy && (
        <Route
          path={routes.me.policies.adjustPolicy.path}
          component={AdjustPolicyFlow}
        />
      )}

      {/* Birdhouse qnr preview -------------------------- */}
      {isStagingOrDev && (
        <Route
          path={routes.birdhouse.preview.adjustPolicy.path}
          component={AdjustPolicyPreview}
        />
      )}
      {isStagingOrDev && (
        <Route
          path={routes.birdhouse.preview.claim.path}
          component={GeneralClaimsPreview}
        />
      )}
      {isStagingOrDev && (
        <Route
          path={routes.birdhouse.preview.policy.path}
          component={PreviewSignupPage}
        />
      )}
      {isStagingOrDev && (
        <Route
          path={routes.birdhouse.preview.policy.noRegion.path}
          component={PreviewSignupPage}
        />
      )}
      {isStagingOrDev && (
        <Route
          path={routes.birdhouse.preview.path}
          component={QuestionnairePreview}
          exact
        />
      )}
      {/* ---------------------------------------------------- */}

      {/*
       * These routes allow us to open a different Intercom Messenger based on the region.
       * The messenger does not allow query params to determine the language or region,
       * so we need to specify these values within the URL path.
       *
       * Note:
       * when adding a new path here,
       * make sure to add a new entry to the routeLocales.ts file
       */}
      <AuthenticatedRoute
        exact={true}
        path={`/:locale${routes.account.path}`}
        component={Account}
      />
      <AuthenticatedRoute
        exact={true}
        path={`/:locale${routes.me.accountSettings.path}`}
        component={AccountSettings}
      />
      <AuthenticatedRoute
        exact={true}
        path={`/:locale${routes.me.policies.detail.path}`}
        component={PolicySingle}
      />
      <AuthenticatedRoute
        exact={false}
        component={BillingHistory}
        path={`/:locale${routes.account.billingHistory.path}`}
      />
      <Route component={PageNotFound} />
    </Switch>
  );
};

export default memo(Routes);
